import './signUpReturningUserPage.scss';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import logo from '../../assets/logo-default.svg';
import { PhoneNumberDisabledInput } from '../../components/shared_new/PhoneNumberDisabledInput/PhoneNumberDisabledInput';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UserExistsExceptionCode } from '../../store/userStore/enums/UserExistsExceptionCode';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { userExistsException } = useUserStore();

  const { primaryPhoneNumber, primaryEmailAddress } = useSignUpStore();

  const existingPhoneNumber = useMemo(() => {
    return (
      userExistsException === UserExistsExceptionCode.PHONE_NUMBER_REGISTERED ||
      userExistsException === UserExistsExceptionCode.BOTH_REGISTERED
    );
  }, [userExistsException]);

  const existingEmailAddress = useMemo(() => {
    return (
      userExistsException ===
        UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED ||
      userExistsException === UserExistsExceptionCode.BOTH_REGISTERED
    );
  }, [userExistsException]);

  const back = (): void => {
    navigate('/sign-up');
  };

  const signIn = (): void => {
    usePinStore.setState({
      primaryPhoneNumber: existingPhoneNumber ? primaryPhoneNumber : null,
    });

    navigate('/sign-in');
  };

  return (
    <div className="sign-up-returning-user-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.signUpReturningUser.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <Link
              data-testid={locator.pages.signUpReturningUser.header.signInLink}
              to="/sign-in"
            >
              {t('SignUpReturningUser.signin')}
            </Link>
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signUpReturningUser.form.root}
              noValidate
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signUpReturningUser.form.h1}>
                  {t('SignUpReturningUser.title')}
                </h1>
                <h2 data-testid={locator.pages.signUpReturningUser.form.h2}>
                  {t('SignUpReturningUser.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  {existingPhoneNumber && (
                    <div style={{ marginBottom: '2rem' }}>
                      <PhoneNumberDisabledInput
                        inputLabelKey="SignUpReturningUser.phone"
                        inputProps={{
                          'data-testid':
                            locator.pages.signUpReturningUser.form.phoneInput,
                        }}
                        value={primaryPhoneNumber}
                      />
                    </div>
                  )}

                  {existingEmailAddress && (
                    <TextField
                      color="primary"
                      inputProps={{
                        'data-testid':
                          locator.pages.signUpReturningUser.form.emailInput,
                      }}
                      label={t('SignUpReturningUser.email')}
                      style={{ pointerEvents: 'none' }}
                      value={primaryEmailAddress}
                      variant="outlined"
                      disabled
                      fullWidth
                    />
                  )}
                </div>

                <div className="inline-buttons">
                  <LoadingButton
                    color="secondary"
                    data-testid={
                      locator.pages.signUpReturningUser.form.backButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={back}
                  >
                    {t('SignUpReturningUser.back')}
                  </LoadingButton>

                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.signUpReturningUser.form.signInButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={signIn}
                  >
                    {t('SignUpReturningUser.signin')}
                  </LoadingButton>
                </div>
              </div>
              <footer
                data-testid={locator.pages.signUpReturningUser.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpReturningUserPage';
